// MODIFIED: this was a second copy of the "new car" form that never received any data.
// It is now what the View button implies: a read-only detail card for one model,
// loaded by id so it can show the back-of-package image too.
import React, { useState, useEffect } from 'react';

import axios, { getErrorMessage } from '../../api/axios';

export default function CarCard({ isVisible, onClose, id, onAddToCollection }) {
    const [car, setCar] = useState(null);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    // ADDED: the casting facts pulled from the Hot Wheels wiki, and whether the
    // release list below them is expanded. It starts closed so the card stays short.
    const [wiki, setWiki] = useState(null);
    const [isWikiLoading, setIsWikiLoading] = useState(false);
    const [showVersions, setShowVersions] = useState(false);
    // ADDED: what this casting is currently asking on eBay. null means eBay is not set up,
    // is unreachable, or has nothing listed - the card then shows the catalog price.
    const [market, setMarket] = useState(null);
    const [isMarketLoading, setIsMarketLoading] = useState(false);
    // ADDED: the eBay listings under the market figures fold away, closed by default
    const [showListings, setShowListings] = useState(false);

    useEffect(() => {
        if (!isVisible || !id) return;

        setIsLoading(true);
        setError('');
        axios.get(`/cars/${id}`)
            .then((response) => setCar(response.data.car))
            .catch((err) => setError(getErrorMessage(err, 'Could not load this model.')))
            .finally(() => setIsLoading(false));
    }, [isVisible, id]);

    // ADDED: the wiki lookup is a separate request so a slow or unreachable wiki never
    // holds up the model's own details. The endpoint answers wiki:null rather than
    // failing, and the whole block is simply left out in that case.
    useEffect(() => {
        if (!isVisible || !id) return;

        setWiki(null);
        setShowVersions(false);
        setIsWikiLoading(true);

        axios.get(`/cars/${id}/wiki`)
            .then((response) => setWiki(response.data.wiki))
            .catch(() => setWiki(null))
            .finally(() => setIsWikiLoading(false));
    }, [isVisible, id]);

    // ADDED: a third independent request, for the same reason as the wiki one - a slow
    // eBay must not hold up the model's own details.
    useEffect(() => {
        if (!isVisible || !id) return;

        setMarket(null);
        setShowListings(false); // ADDED: every card opens with the listings folded
        setIsMarketLoading(true);

        axios.get(`/cars/${id}/market`)
            .then((response) => setMarket(response.data.market))
            .catch(() => setMarket(null))
            .finally(() => setIsMarketLoading(false));
    }, [isVisible, id]);

    // ADDED: opens eBay's own "sell an item" flow with this casting as the starting
    // keyword, so the listing begins from the right product instead of a blank page.
    //
    // Two deliberate limits. eBay's prelist URL accepts a keyword and nothing else, so the
    // price, condition and photos are still filled in on eBay - there is no public URL
    // that carries them. And this link never gets an affiliate tag: EPN excludes traffic
    // to your own listings as self-referral, and tagging it would put the account at risk.
    const sellOnEbayUrl = car
        ? `https://www.ebay.com/sl/prelist/suggest?keyword=${encodeURIComponent(
            [car.datereleased, 'Hot Wheels', car.model, car.name, car.color].filter(Boolean).join(' ')
          )}`
        : '';

    if (!isVisible) return null;

    // Label/value pairs rendered in the details grid
    const details = car ? [
        { label: 'Series code', value: car.seriesId },
        { label: 'Make', value: car.brand },
        { label: 'Model', value: car.model },
        { label: 'Colour', value: car.color },
        { label: 'Scale', value: car.modelScale },
        { label: 'Year', value: car.datereleased },
        { label: 'Package series', value: car.hwcollectionName },
        // MODIFIED: the market value takes this slot when eBay has listings for the
        // casting. It is an ASKING price from active listings, never a sold price, and the
        // label says so. Collection totals keep using the catalog price.
        market
            ? { label: 'Market (avg asking)', value: `$${Number(market.average).toFixed(2)}` }
            // MODIFIED: an unpriced model says so rather than claiming to be worth $0.00
            : {
                label: 'Catalog price',
                value: car.price === null || car.price === undefined
                    ? 'Not priced'
                    : `$${Number(car.price).toFixed(2)}`,
              },
    ] : [];

    // MODIFIED (styling): the modal now uses the shared card/button classes and the
    // theme tokens, so it is readable in dark mode and matches the rest of the app.
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="w-full max-w-lg cv-card overflow-hidden relative max-h-[90vh] overflow-y-auto animate-fade-up">
                <div className="cv-stripe sticky top-0" />
                <button onClick={onClose}
                    aria-label="Close"
                    className="absolute top-3 right-3 cv-muted hover:text-brand-flame text-3xl leading-none px-2 z-10 transition duration-300">
                    &times;
                </button>

                <div className="px-5 py-6 sm:px-6">
                    {isLoading && <p className="text-center cv-muted py-10">Loading...</p>}
                    {error && <p className="cv-alert-error text-center">{error}</p>}

                    {car && !isLoading &&
                        <>
                            <p className="cv-eyebrow">{car.hwcollectionName}</p>
                            <h1 className="cv-display text-2xl sm:text-3xl mb-4 pr-8">{car.name}</h1>

                            {/* Front and back of the package, side by side from sm up */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                                {[
                                    { src: car.imageFront, label: 'Front' },
                                    { src: car.imageBack, label: 'Back' },
                                ].map((image) => (
                                    <div key={image.label} className="space-y-1">
                                        <div className="cv-well w-full h-44 rounded-xl flex items-center justify-center overflow-hidden">
                                            {image.src
                                                ? <img src={image.src} alt={`${car.name} ${image.label}`} className="w-full h-full object-contain" />
                                                : <span className="text-xs cv-muted">No {image.label.toLowerCase()} image</span>}
                                        </div>
                                        <p className="text-xs cv-muted text-center">{image.label}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                {details.map((detail) => (
                                    <div key={detail.label}>
                                        <p className="text-[0.65rem] cv-muted uppercase tracking-widest">{detail.label}</p>
                                        <p className="cv-strong break-words">{detail.value || '\u2014'}</p>
                                    </div>
                                ))}
                            </div>

                            {/* ADDED: the market panel - the spread behind the average above.
                                Everything here is what sellers are ASKING right now; eBay does
                                not make completed sale prices available outside a closed
                                partner programme, so no sold comps are shown or implied. */}
                            {isMarketLoading && !market &&
                                <p className="text-xs cv-muted mt-6">Checking the market...</p>
                            }

                            {market &&
                                <div className="mt-6 pt-5" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                                    <p className="cv-eyebrow">Market value</p>

                                    <div className="grid grid-cols-3 gap-x-4 gap-y-3 mt-3">
                                        {[
                                            { label: 'Lowest', value: market.low },
                                            { label: 'Average', value: market.average },
                                            { label: 'Highest', value: market.high },
                                        ].map((figure) => (
                                            <div key={figure.label}>
                                                <p className="text-[0.65rem] cv-muted uppercase tracking-widest">{figure.label}</p>
                                                <p className="cv-strong font-display font-bold">${Number(figure.value).toFixed(2)}</p>
                                            </div>
                                        ))}
                                    </div>

                                    {/* ADDED: the cheapest few listings behind those figures, so the
                                        number is something you can act on rather than just read.
                                        They come from the same API response as the averages - no
                                        extra request - and lots are already excluded, so the
                                        cheapest row is a single car. */}
                                    {/* MODIFIED: the listings are behind a toggle, closed by default, so
                                        the card leads with the three figures. Same toggle style as
                                        "Other releases" further down. */}
                                    {market.listings && market.listings.length > 0 &&
                                        <div className="mt-4">
                                        <button
                                            type="button"
                                            onClick={() => setShowListings(!showListings)}
                                            aria-expanded={showListings}
                                            className="cv-btn cv-btn-quiet w-full justify-between text-sm">
                                            <span>Cheapest new listings ({market.listings.length})</span>
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                                 strokeWidth={2} stroke="currentColor"
                                                 className={`w-4 h-4 transition-transform duration-300 ${showListings ? 'rotate-180' : ''}`}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                                            </svg>
                                        </button>
                                        {showListings &&
                                        <ul className="mt-2 rounded-xl overflow-hidden animate-fade-up" style={{ border: '1px solid var(--border-subtle)' }}>
                                            {market.listings.map((listing, index) => (
                                                <li key={listing.id}
                                                    style={{ borderTop: index === 0 ? 'none' : '1px solid var(--border-subtle)' }}>
                                                    <a href={listing.url} target="_blank" rel="noopener noreferrer sponsored"
                                                       className="flex items-center gap-3 px-3 py-2 hover:bg-[var(--accent-soft)] transition duration-300">
                                                        <div className="cv-well w-10 h-10 rounded-lg shrink-0 flex items-center justify-center overflow-hidden">
                                                            {listing.image
                                                                ? <img src={listing.image} alt="" className="w-full h-full object-contain" />
                                                                : <span className="text-[0.6rem] cv-muted">none</span>}
                                                        </div>
                                                        <span className="flex-1 min-w-0">
                                                            <span className="block text-xs cv-body truncate">{listing.title}</span>
                                                            {listing.condition &&
                                                                <span className="block text-[0.65rem] cv-muted truncate">{listing.condition}</span>}
                                                        </span>
                                                        <span className="font-display font-bold cv-strong text-sm shrink-0">
                                                            ${Number(listing.price).toFixed(2)}
                                                        </span>
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                        }
                                        </div>
                                    }

                                    <p className="text-xs cv-muted mt-3 leading-relaxed">
                                        {/* MODIFIED: says "new" now that used listings are filtered out */}
                                        Asking prices from {market.listingCount} new eBay {market.listingCount === 1 ? 'listing' : 'listings'}
                                        {market.currency !== 'USD' && ` in ${market.currency}`}
                                        , not sold prices.
                                        {car.price !== null && car.price !== undefined &&
                                            ` Catalog price $${Number(car.price).toFixed(2)}.`}
                                        {' '}
                                        <a href={market.searchUrl} target="_blank" rel="noopener noreferrer" className="cv-accent underline">
                                            See the listings
                                        </a>
                                    </p>
                                </div>
                            }

                            {/* ADDED: the four facts from the wiki, kept deliberately short.
                                CC BY-SA content, so the source is credited with a link back. */}
                            {isWikiLoading &&
                                <p className="text-xs cv-muted mt-6">Looking this casting up...</p>
                            }

                            {wiki &&
                                <div className="mt-6 pt-5" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                                    <p className="cv-eyebrow">About this casting</p>

                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 mt-3">
                                        {[
                                            { label: 'Designed by', value: wiki.designer },
                                            { label: 'Debut', value: wiki.debutSeries },
                                            { label: 'Produced', value: wiki.years },
                                        ].filter((fact) => fact.value).map((fact) => (
                                            <div key={fact.label}>
                                                <p className="text-[0.65rem] cv-muted uppercase tracking-widest">{fact.label}</p>
                                                <p className="cv-strong text-sm break-words">{fact.value}</p>
                                            </div>
                                        ))}
                                    </div>

                                    {wiki.description &&
                                        <p className="text-sm cv-body mt-4 leading-relaxed">{wiki.description}</p>
                                    }

                                    <p className="text-xs cv-muted mt-3">
                                        Source:{' '}
                                        <a href={wiki.url} target="_blank" rel="noopener noreferrer" className="cv-accent underline">
                                            Hot Wheels Wiki
                                        </a>
                                        {' '}&middot; CC BY-SA
                                    </p>

                                    {/* The release list, collapsed by default */}
                                    {wiki.versions.length > 0 &&
                                        <div className="mt-4">
                                            <button
                                                type="button"
                                                onClick={() => setShowVersions(!showVersions)}
                                                aria-expanded={showVersions}
                                                className="cv-btn cv-btn-quiet w-full justify-between text-sm">
                                                <span>Other releases ({wiki.versions.length})</span>
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                                     strokeWidth={2} stroke="currentColor"
                                                     className={`w-4 h-4 transition-transform duration-300 ${showVersions ? 'rotate-180' : ''}`}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                                                </svg>
                                            </button>

                                            {showVersions &&
                                                <ul className="mt-2 max-h-72 overflow-y-auto rounded-xl animate-fade-up"
                                                    style={{ border: '1px solid var(--border-subtle)' }}>
                                                    {wiki.versions.map((version, index) => (
                                                        <li key={`${version.toyNumber}-${version.color}-${index}`}
                                                            className="flex items-center gap-2 px-3 py-2 text-xs"
                                                            style={{
                                                                borderTop: index === 0 ? 'none' : '1px solid var(--border-subtle)',
                                                                backgroundColor: version.isCurrent ? 'var(--accent-soft)' : 'transparent',
                                                            }}>
                                                            <span className="font-display font-bold cv-strong w-9 shrink-0">{version.year}</span>
                                                            <span className="flex-1 min-w-0">
                                                                <span className="block cv-body truncate">{version.color || '\u2014'}</span>
                                                                <span className="block cv-muted truncate">{version.series}</span>
                                                            </span>
                                                            {version.isCurrent &&
                                                                <span className="shrink-0 cv-accent font-semibold whitespace-nowrap">this one</span>
                                                            }
                                                            {!version.isCurrent && version.ownedQuantity > 0 &&
                                                                <span className="shrink-0 bg-brand-flame text-white px-1.5 py-0.5 rounded font-display font-bold whitespace-nowrap">
                                                                    owned &times;{version.ownedQuantity}
                                                                </span>
                                                            }
                                                            {!version.isCurrent && !version.ownedQuantity && version.inCatalog &&
                                                                <span className="shrink-0 cv-muted whitespace-nowrap">in catalog</span>
                                                            }
                                                        </li>
                                                    ))}
                                                </ul>
                                            }
                                        </div>
                                    }
                                </div>
                            }

                            {/* MODIFIED: the two things you can do with a model you are looking
                                at - shelve it, or sell it. */}
                            <div className="mt-6 space-y-2">
                                {onAddToCollection &&
                                    <button
                                        onClick={() => onAddToCollection(car)}
                                        className="cv-btn cv-btn-primary w-full py-3 uppercase">
                                        Add to a collection
                                    </button>
                                }
                                <a
                                    href={sellOnEbayUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="cv-btn cv-btn-outline w-full py-3 uppercase">
                                    Sell this on eBay
                                </a>
                            </div>
                        </>
                    }
                </div>
            </div>
        </div>
    );
}
